/*
  © 2024–2026 Atlas PHM
  Independently developed software. All rights reserved.
*/

/**
 * Technical Appendix Content (Layer 3)
 *
 * Interactive deep dives covering technical concepts from the PDF appendices.
 * These contain the same substance as the printed appendices but are
 * structured for interactive exploration with visualizations.
 *
 * Topics:
 * 1. Modulation and cycling behavior
 * 2. Clean systems and efficiency
 * 3. Power flushing and laminar flow filters
 * 4. Pipework, flow, and velocity
 * 5. Same comfort, different stress and longevity
 */

export interface TechnicalAppendixSection {
  title: string;
  paragraphs: string[];
  keyPoint?: string;
}

export interface TechnicalAppendixData {
  id: string;
  title: string;
  subtitle: string;
  icon: string;
  introduction: string;
  visualizationType: 'modulation-cycling' | 'clean-system' | 'pipework-flow' | 'stress-longevity';
  sections: TechnicalAppendixSection[];
}

export const technicalAppendixContent: Record<string, TechnicalAppendixData> = {
  'modulation-cycling': {
    id: 'modulation-cycling',
    title: 'Modulation and Cycling Behavior',
    subtitle: 'How heating systems adjust their output',
    icon: '⚙️',
    introduction: 'Modern heating systems can vary their output rather than simply switching on and off. Understanding the difference between modulation and cycling helps explain why some systems feel more comfortable and last longer than others.',
    visualizationType: 'modulation-cycling',
    sections: [
      {
        title: 'What is modulation?',
        paragraphs: [
          'A modulating boiler or heat pump can adjust its heat output continuously across a range — typically from 30% to 100% of maximum capacity. Instead of running at full power until the home is warm, then switching off, it finds the exact output needed to maintain temperature.',
          'This is like driving a car at a steady speed versus constantly accelerating and braking. Both get you there, but steady driving is more efficient and creates less wear.',
          'Modulation requires communication between the boiler and its controls. Load-compensating thermostats measure how far the room is from the target temperature and tell the boiler how much heat to produce. The result is smoother comfort with less wasted energy.',
        ],
        keyPoint: 'Modulating systems match their output to the demand, rather than overshooting and then stopping.',
      },
      {
        title: 'What is cycling?',
        paragraphs: [
          'Cycling occurs when a heating system turns on and off frequently — sometimes dozens of times per hour. This happens when the heat source is oversized for the current demand, or when controls are too basic to communicate the actual need.',
          'Each startup puts stress on components: the fan spins up, the gas valve opens, the igniter fires, the heat exchanger rapidly changes temperature. Over thousands of cycles, these components wear faster than they would under steady operation.',
          'Cycling also wastes energy. The boiler fires at full power, overshoots the target, shuts down, then the home cools below target and it fires again. This sawtooth pattern is less efficient than maintaining a steady temperature.',
        ],
        keyPoint: 'Frequent cycling wastes energy and accelerates component wear.',
      },
      {
        title: 'Why does sizing matter?',
        paragraphs: [
          'An oversized boiler reaches its minimum output long before the home is satisfied, forcing it to cycle. A correctly sized system can modulate down to match the demand and run continuously at low output.',
          'For example, a 30kW boiler in a home that only needs 8kW will spend most of its time cycling, even if it can modulate. The same home with a properly sized 12kW boiler could run smoothly at 60-70% output instead.',
          'This is why modern condensing boilers — which achieve their best efficiency at low outputs — benefit most from correct sizing. An oversized condensing boiler may never reach its full efficiency potential.',
        ],
        keyPoint: 'The best boiler for your home is often smaller than you expect.',
      },
    ],
  },

  'clean-systems': {
    id: 'clean-systems',
    title: 'Clean Systems and Efficiency',
    subtitle: 'Why water quality affects performance',
    icon: '💧',
    introduction: 'The water circulating through your heating system affects efficiency, reliability, and lifespan. Understanding what happens inside the system helps explain why maintenance and water treatment matter.',
    visualizationType: 'clean-system',
    sections: [
      {
        title: 'What makes water "dirty"?',
        paragraphs: [
          'Heating system water picks up contaminants over time: dissolved iron from radiators, copper from pipes, limescale from hard water, and sludge from corrosion. These build up in the heat exchanger, pump, valves, and radiators.',
          'In the heat exchanger — where the flame heats the water — deposits act as insulation. The boiler has to work harder to transfer heat through this layer, reducing efficiency and increasing stress on components.',
          'Sludge settles at the bottom of radiators, creating cold spots. The system compensates by running longer or hotter, wasting energy to overcome the blockage.',
        ],
        keyPoint: 'Dirty water makes your boiler work harder to deliver the same heat.',
      },
      {
        title: 'Impact on modulation',
        paragraphs: [
          'Clean water allows precise heat transfer. The boiler can accurately sense water temperature and adjust its output smoothly. With dirty water, temperature readings become unreliable and modulation becomes erratic.',
          'A contaminated system may cycle more frequently as the boiler struggles to maintain stable temperatures. It might fire up strongly, overshoot due to poor heat transfer, shut down, then repeat the cycle unnecessarily.',
          'This is why a well-maintained system often feels more comfortable — the controls can do their job properly when the underlying system is clean.',
        ],
        keyPoint: 'Clean systems modulate smoothly; dirty systems cycle unnecessarily.',
      },
      {
        title: 'Maintaining system health',
        paragraphs: [
          'Power flushing clears accumulated sludge and debris from the system. It is typically done before installing a new boiler to protect the new components from inherited contamination.',
          'Inhibitor chemicals prevent corrosion and scale formation. They should be topped up at each service and tested periodically to ensure adequate protection.',
          'Magnetic filters (like MagnaClean) continuously capture iron oxide particles before they can settle in the system. They should be cleaned at each annual service.',
          'In hard water areas, a scale reducer or softener protects the heat exchanger from limescale buildup, which is especially important for combi boilers that heat mains water directly.',
        ],
        keyPoint: 'Regular maintenance prevents small problems from becoming expensive failures.',
      },
    ],
  },

  'power-flush-filters': {
    id: 'power-flush-filters',
    title: 'Power Flushing & Laminar Flow Filters',
    subtitle: 'Keeping water clean and flow steady',
    icon: '🧼',
    introduction: 'Power flushing and laminar flow filters work together to keep your system water clean, protect components, and maintain smooth circulation. Grouped together, they explain how systems recover from existing sludge and stay clean long-term.',
    visualizationType: 'clean-system',
    sections: [
      {
        title: 'Power flushing clears the backlog',
        paragraphs: [
          'A power flush uses high-velocity water and cleaning agents to lift sludge from radiators, pipework, and the heat exchanger. It is typically done before installing new equipment so that old debris does not contaminate new components.',
          'Clearing sludge restores heat transfer, reduces cold spots, and lets the boiler or heat pump modulate accurately again.',
        ],
        keyPoint: 'Power flushing removes historic debris so the system can reset to a clean baseline.',
      },
      {
        title: 'Laminar flow filters keep circulation smooth',
        paragraphs: [
          'Laminar flow filters are inline filters that slow and straighten the water path, trapping fine particles while maintaining steady flow. This reduces turbulence and protects pumps, valves, and heat exchangers from abrasive debris.',
          'By maintaining smoother flow, they also help systems stay quiet and balanced, especially when running at lower temperatures.',
        ],
        keyPoint: 'Laminar flow filters capture fine debris without disrupting circulation.',
      },
      {
        title: 'Together they prevent repeat problems',
        paragraphs: [
          'A power flush is a deep clean. Filters are the ongoing protection. When combined with inhibitor chemicals, they keep the system clean for the long term.',
          'If the system already has a magnetic filter, it should still be cleaned regularly so it keeps working effectively.',
        ],
        keyPoint: 'Deep clean once, protect continuously.',
      },
    ],
  },

  'pipework-flow': {
    id: 'pipework-flow',
    title: 'Pipework, Flow, and Velocity',
    subtitle: 'How pipe sizing affects comfort and noise',
    icon: '🔧',
    introduction: 'The pipes connecting your boiler to radiators carry heat around the home. Their size affects how quietly and effectively heat is delivered, and whether modern efficiency features can work properly.',
    visualizationType: 'pipework-flow',
    sections: [
      {
        title: 'Flow rate and velocity',
        paragraphs: [
          'For any given heat demand, water must flow through the pipes to deliver that heat. The same volume of water flowing through a smaller pipe travels faster — like water through a garden hose versus a fire hose.',
          'Higher velocity means more turbulence, more noise, and more stress on pipe joints and valves. It also makes it harder for radiators to extract heat evenly from the water flow.',
          'Microbore systems (8mm or 10mm pipes) were popular for ease of installation but create high velocities at typical flow rates. Modern 15mm or 22mm pipework allows the same heat delivery with gentler flow.',
        ],
        keyPoint: 'Larger pipes carry the same heat with less noise and turbulence.',
      },
      {
        title: 'Low-temperature heating',
        paragraphs: [
          'Condensing boilers achieve their best efficiency when return water is below 55°C, which allows the flue gases to condense and recover latent heat. Heat pumps work best at even lower temperatures — typically 35-45°C.',
          'To deliver sufficient heat at lower temperatures, you need either larger radiators or higher flow rates. If pipes are too small for the required flow, you cannot run the system at optimal efficiency.',
          'Underfloor heating naturally uses low temperatures because of its large surface area. Radiators may need to be oversized — or pipes upgraded — to achieve the same effect.',
        ],
        keyPoint: 'Low-temperature efficiency requires adequate pipe sizing.',
      },
      {
        title: 'Noise and comfort',
        paragraphs: [
          'Water rushing through pipes at high velocity creates audible noise — especially at bends, through radiator valves, and where pipes pass through walls or floors. This is often described as "rushing" or "whistling" sounds.',
          'Beyond noise, high-velocity flow makes it harder to balance the system so that each radiator gets its fair share of heat. Some rooms may overheat while others struggle.',
          'If you notice uneven heating or water noise, pipe sizing may be a contributing factor — especially in older microbore systems or extensions where pipework was undersized.',
        ],
        keyPoint: 'Quieter, more even heating often comes from properly sized pipework.',
      },
    ],
  },

  'stress-longevity': {
    id: 'stress-longevity',
    title: 'Same Comfort, Different Stress',
    subtitle: 'Why design affects how long systems last',
    icon: '⏱️',
    introduction: 'Two heating systems can maintain the same temperature and deliver the same hot water, yet one lasts significantly longer than the other. The difference lies in how the work is distributed — steady versus spiky operation.',
    visualizationType: 'stress-longevity',
    sections: [
      {
        title: 'Thermal stress',
        paragraphs: [
          'Every time a boiler fires, the heat exchanger rapidly heats up. When it stops, it cools down. These temperature swings create thermal stress — expansion and contraction that gradually weaken the metal.',
          'A boiler that runs steadily at 50% output for an hour experiences far less thermal cycling than one that fires at 100% for 10 minutes, six times in the same hour. Both deliver similar heat, but the mechanical cost is very different.',
          'Heat pumps experience this less dramatically because they operate at lower temperatures with gentler changes, which is one reason they often achieve long lifespans with proper maintenance.',
        ],
        keyPoint: 'Steady operation creates less thermal stress than repeated hot-cold cycles.',
      },
      {
        title: 'Mechanical wear',
        paragraphs: [
          'Beyond temperature, each startup involves mechanical actions: the fan accelerates, the gas valve opens, the igniter fires, pumps adjust speed. Each action has a finite lifespan measured in cycles.',
          'A fan rated for 100,000 start-stop cycles will wear out faster in a system that cycles 30 times per hour than one that runs continuously. The same applies to valves, pumps, and other moving parts.',
          'This is why correctly sized, well-controlled systems tend to need fewer repairs — not because the components are better, but because they are used more gently.',
        ],
        keyPoint: 'Components rated for cycles last longer in systems that cycle less.',
      },
      {
        title: 'Design choices and outcomes',
        paragraphs: [
          'Compact combi boilers prioritise space efficiency but work harder for hot water. System boilers with cylinders spread the load across more components and can operate more steadily.',
          'Heat pumps are designed for steady, continuous operation and suffer when asked to cycle frequently. Correct sizing and buffer tanks help maintain smooth operation.',
          'There is no single "best" design — each represents a trade-off between compactness, responsiveness, efficiency, and longevity. Understanding these trade-offs helps match the right system to your priorities.',
        ],
        keyPoint: 'Different designs optimise for different priorities — choose based on what matters to you.',
      },
    ],
  },
};

/**
 * Get all technical appendix IDs for navigation
 */
export function getTechnicalAppendixIds(): string[] {
  return Object.keys(technicalAppendixContent);
}

/**
 * Get technical appendix by ID
 */
export function getTechnicalAppendix(id: string): TechnicalAppendixData | null {
  return technicalAppendixContent[id] || null;
}
